import React, { useEffect, useState } from 'react';
import {
    Button, ButtonGroup, ClickAwayListener, Grow, Paper, Popper, MenuItem, MenuList, CircularProgress
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { CircularProgressWithLabel } from '../..';
import './SplitButton.scss';

/**
 * @param {{
 *  options: {label: string, value: number, disabled: boolean, isDisabledAndShowProgress?: boolean, isProcessing?: boolean }[];
 *  color: (import('@mui/material').PropTypes.Color);
 *  handleClick: Function;
 *  disabled?: boolean;
 *  className?: string;
 *  defaultValue?: {label: string, value: number, disabled: boolean, isDisabledAndShowProgress?: boolean, isProcessing?: boolean};
 *  showChevron?: boolean;
 *  progressLabel?: string;
 *  progressValue?: number;
 *  isProcessing?: boolean;
 * }} props
 */
const SplitButton = ({
    options,
    color,
    className,
    handleClick = () => { },
    disabled = false,
    defaultValue = options[0],
    showChevron = true,
    progressLabel,
    progressValue = undefined,
}) => {
    const anchorRef = React.useRef(null);
    const [open, setOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(defaultValue);

    useEffect(() => {
        if (showChevron && options?.length > 0) {
            setSelectedOption(options.find((x) => x.value === selectedOption?.value) || defaultValue);
        }
    }, [JSON.stringify(options)]);

    if (!options || options.length === 0) {
        return null;
    }

    const effectiveOption = selectedOption ?? options[0];

    if (options.length <= 1)
    {
        showChevron = false;
    }

    const handleOptionClick = (option) => {
        showChevron
            ? setSelectedOption(option)
            : handleClick(option);
        setOpen(false);
    };

    // Main button always executes the selected option
    // The chevron button (when visible) opens the dropdown to change selection
    let buttonGroupOnClick = handleClick;

    return (
        <>
            <ButtonGroup
                className={`split-button-container ${className || ''}`}
                variant="contained"
                color={color}
                ref={anchorRef}
                size="large"
                aria-label="outlined primary button group"
                disabled={disabled}
                style={{ boxShadow: 'none' }}
            >
                <Button
                    onClick={() => buttonGroupOnClick(effectiveOption)}
                    disabled={effectiveOption.disabled}
                    className="split-button-stick-together-label"
                >
                    <span>{effectiveOption.isDisabledAndShowProgress ? `${progressLabel}` : `${effectiveOption.label}`}</span>
                    {effectiveOption.isDisabledAndShowProgress && progressValue !== undefined && (
                        <div className="split-button-progress">
                            <CircularProgressWithLabel
                                size={30}
                                value={progressValue}
                            />
                        </div>
                    )}
                    {effectiveOption.isProcessing && (
                        <div className="split-button-progress">
                            <CircularProgress
                                color="secondary"
                                size={20}
                            />
                        </div>
                    )}
                </Button>
                {showChevron &&
                    (<Button
                        onClick={() => setOpen(true)}
                        className="split-button-stick-together-arrow"
                        disabled={!!options.find(x => x.isDisabledAndShowProgress)}
                    >
                        <FontAwesomeIcon icon={faChevronDown} />
                    </Button>)
                }
            </ButtonGroup>
            <Popper
                open={open}
                anchorEl={anchorRef.current}
                placement="bottom-start"
                transition
                className="split-button-popper"
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: placement === 'bottom' ? 'center top' : 'right bottom',
                        }}
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={() => setOpen(false)}>
                                <MenuList>
                                    {options.map((option) => (
                                        <MenuItem
                                            key={option.value}
                                            selected={option.value === effectiveOption.value}
                                            onClick={() => handleOptionClick(option)}
                                            disabled={disabled || option.disabled}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    );
};

export default SplitButton;
